import { useNavigation } from '@react-navigation/core';
import { Button, Meta, theme } from 'aimo-ui';
import TouchableItem from 'components/common/TouchableItem';
import FlatListCustom from 'components/sheets/FlatListCustom';
import AccountContext from 'context/AccountContext';
import React, { ReactElement, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useCityParkingStore } from 'stores/cityParkingStore';
import styled from 'styled-components/native';
import { Divider } from 'styles/CommonLayoutStyles';
import {
  ColumnWithMargins,
  MenuRowWrapper,
  StyledWrapper,
} from 'styles/ContainerStyles';
import { LegallAddress, Maybe, Pmc } from 'types/generatedSchemaTypes';
import { CityParkingNavProps } from 'types/navigation';
import { getCustomerAddress } from 'utils/cityParkingUtils';

const ColoredContainer = styled(StyledWrapper)`
  background-color: ${(p) => p.theme.colors.gray50};
  flex-direction: row;
  justify-content: space-between;
  padding: 10px;
  border-radius: 10px;
`;

const ContainerItemWrapper = styled.View<{
  width?: string;
}>`
  width: ${(p) => (p.width ? p.width + 'px' : 'auto')};
  justify-content: center;
  align-items: center;
`;

const MetaText = styled(Meta)`
  padding-right: 2px;
`;

const DividerMargin0: React.FC = () => <Divider marginHorizontal={0} />;
interface PurchaseOrAuthenticateProps {
  addressList: LegallAddress[];
  purchaseablePmcs: Maybe<Pmc>[];
  renderItem: (item: Pmc, canPurchase: boolean) => ReactElement;
}

const PurchaseOrAuthenticate: React.FC<PurchaseOrAuthenticateProps> = ({
  addressList,
  purchaseablePmcs,
  renderItem,
}) => {
  const { customer } = useContext(AccountContext);
  const searchTerm = useCityParkingStore((state) => state.searchTerm);
  const { t } = useTranslation();
  const strongAuthFlow = useCityParkingStore(
    (state) => state.setStrongAuthFlow
  );
  const { navigate } = useNavigation<CityParkingNavProps>();

  const handleOpenAuthModal = () => {
    strongAuthFlow(true);
    navigate('homeAddress');
  };

  const isAuthenticated = customer.strongAuthentication?.gedi;
  const hasAddressList = addressList.length > 0;
  const hasSearchTerm = searchTerm !== '';
  const hasPurchaseablePmcs = purchaseablePmcs.length > 0;

  const renderAuthenticationRequired = () => (
    <ColoredContainer>
      <ContainerItemWrapper width="200">
        <Meta>{t('cityPermits.needAddress')}</Meta>
      </ContainerItemWrapper>
      <ContainerItemWrapper width="80">
        <Button
          size="s"
          text={t('button.identify')}
          onPress={handleOpenAuthModal}
        />
      </ContainerItemWrapper>
    </ColoredContainer>
  );

  const renderPurchaseablePmcs = () => {
    const addressString = getCustomerAddress(customer?.strongAuthentication);

    return (
      <ColumnWithMargins mb={25}>
        <ColumnWithMargins mt={10} mb={10}>
          <TouchableItem
            justifyContent="flex-start"
            onPress={handleOpenAuthModal}>
            <MenuRowWrapper>
              <MetaText
                testID="city-pmcs-buyable-meta"
                color={theme.colors.gray200}
                numberOfLines={1}>
                {t('cityPermits.suggestionsList', {
                  forAddress: 'for ',
                })}
              </MetaText>
              <Meta color={theme.colors.link} numberOfLines={1}>
                {addressString}
              </Meta>
            </MenuRowWrapper>
          </TouchableItem>
        </ColumnWithMargins>

        {hasPurchaseablePmcs ? (
          <FlatListCustom
            data={purchaseablePmcs as Pmc[]}
            windowSize={10}
            ItemSeparatorComponent={DividerMargin0}
            renderItem={({ item }) => renderItem(item, true)}
            keyExtractor={(item, index) => `${item?.id}-${index}`}
            testID="city-pmcs-purchaseable-list"
          />
        ) : (
          <StyledWrapper maxWidth={400}>
            <Meta>{t('cityPermits.addressNotCovered')}</Meta>
          </StyledWrapper>
        )}
      </ColumnWithMargins>
    );
  };

  const renderContent = () => {
    if (!isAuthenticated) {
      return renderAuthenticationRequired();
    }

    if (hasAddressList && !hasSearchTerm) {
      return renderPurchaseablePmcs();
    }

    return null;
  };

  return <>{renderContent()}</>;
};

export default PurchaseOrAuthenticate;
